/**
 * Edge quality-gate operating points — ONE place, `Rule-based`, PROVISIONAL.
 *
 * FSD §4.3 / §6.3: no threshold ships as final without a measured table from the shadow phase
 * (300–500 labelled visits across premises kinds × metro/non-metro × day/night). Until that table
 * exists these floors are the droom's S7-6 calibration carried over (single-device reasoning) and
 * every gate result is logged WITH its threshold so the reviewer sees the operating point, never a
 * bare verdict. They stop obviously unusable frames before upload; they are a UX gate, not evidence.
 */

/** Exposure band (mean luma 0–255). Below = murk, above = glare wash. Shared by stills and clips. */
export const LUMA_MIN = 45;
export const LUMA_MAX = 210;

export type StillKind = 'SV_STILL' | 'SV_SELFIE' | 'SV_ID_DOC';

/** Per-kind still floors: shorter side in px, variance of the 4-neighbour Laplacian, and — where a kind has one —
 *  the ceiling on the clipped-highlight ratio (glare). `maxClippedRatio` absent ⇒ the glare gate does not run. */
export const STILL_GATES: Readonly<Record<StillKind, { minShortSide: number; minLaplacianVar: number; maxClippedRatio?: number }>> = {
  // Premises stills (signboard, door plate, certificate wall): text must survive review.
  SV_STILL: { minShortSide: 480, minLaplacianVar: 40 },
  // Faces are low-texture; 25 still fails heavy defocus without blocking normal selfies.
  SV_SELFIE: { minShortSide: 480, minLaplacianVar: 25 },
  // Identity document: the droom DOC_PHOTO floor, plus a glare ceiling — a lamp/window reflection on a laminated
  // PAN/Aadhaar card blows out the printed number and photo the server's OCR and face crop need. 0.15 is
  // PROVISIONAL: a well-exposed white card rarely clips at all; a reflection band clips a large patch. It is a
  // UX gate (retake with a named reason), never evidence — the server judges quality again.
  SV_ID_DOC: { minShortSide: 600, minLaplacianVar: 60, maxClippedRatio: 0.15 },
};

/** Clip gates, evaluated over the live samples taken while recording (clipQuality.ts). */
export const CLIP_GATES = {
  /** How often the viewfinder is sampled while recording (≤ 4/s per INTAKE-08 §A1). */
  sampleIntervalMs: 500,
  /** Downscale width for the sample (cost discipline on low-end handsets). */
  sampleWidth: 96,
  /** Fraction of samples outside the exposure band above which the clip is refused. */
  badExposureRatioMax: 0.5,
  /** Median Laplacian variance floor. Motion blur on a walking clip is expected; this catches a
   *  lens-covered / totally defocused clip, not hand shake. */
  minMedianLaplacianVar: 12,
  /** Sample floor below which the gate cannot honestly judge (→ NOT_EVALUATED, never a pass). */
  minSamples: 3,
} as const;

/** The version stamp the receipt and telemetry carry so a table change is attributable. */
export const GATE_VERSION = 'svapp-edge-gates/0.2-provisional';
